import { type JSX, type PropsWithChildren, type ReactNode } from 'react';

import { Text, cn } from '@videoblocks/design-system';

export interface GenerationFlowIdleViewProps extends PropsWithChildren {
    title: string;
    subtitle: string;
    /** Visual shown above the copy — an icon tile, or a richer display like AnimatedCardDisplay */
    visual?: ReactNode;
    className?: string;
}

/**
 * Empty state shown before a generation has been started. Siblings in this folder cover the
 * inProgress/success/error states of the same flow.
 */
export const GenerationFlowIdleView = ({
    title,
    subtitle,
    visual,
    className,
    children,
}: GenerationFlowIdleViewProps): JSX.Element => {
    return (
        <div className={cn('size-full flex flex-col items-center justify-center gap-3', className)}>
            {visual}
            <div className="flex flex-col gap-4 items-center">
                <div className="flex flex-col gap-2 items-center text-center max-w-82.5 w-full">
                    <Text>{title}</Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-neutral-dark-700">
                        {subtitle}
                    </Text>
                </div>
                {children}
            </div>
        </div>
    );
};

export interface GenerationFlowIdleIconTileProps {
    /** Rendered at 32x32; the tile owns its color so callers don't repeat the fill override */
    icon: ReactNode;
}

export const GenerationFlowIdleIconTile = ({ icon }: GenerationFlowIdleIconTileProps): JSX.Element => {
    return (
        <div className="size-16 flex items-center justify-center rounded-lg p-4 dark:bg-neutral-dark-200 dark:[&_path]:fill-neutral-dark-700">
            {icon}
        </div>
    );
};
